/**
 * Date formatting utilities
 */
import logger from './logger';

/**
 * Format an ISO date string to a human-readable format
 * @param {string|null} dateString - ISO date string (e.g., "2025-11-02T12:00:00Z")
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string or empty string if null
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    const defaultOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...options,
    };

    return date.toLocaleDateString('en-US', defaultOptions);
  } catch (error) {
    logger.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format a date to include time
 * @param {string|null} dateString - ISO date string
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    logger.error('Error formatting date time:', error);
    return '';
  }
};

/**
 * Format a YYYY-MM-DD date string to M/D/YYYY slash format (e.g. "6/28/2024")
 * Parses the date parts directly to avoid UTC-to-local timezone shifts.
 * Note: If a string does not match the YYYY-MM-DD prefix, it is returned unchanged.
 * @param {string|null} dateString - ISO date string (e.g., "2024-06-28")
 * @returns {string} Formatted date string or empty string if null/invalid
 */
export const formatSlashDate = (dateString) => {
  if (!dateString) return '';
  const match = typeof dateString === 'string' && dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return dateString;
  return `${match[2]}/${match[3]}/${match[1]}`;
};

/**
 * Get relative time string (e.g., "2 hours ago", "3 days ago")
 * @param {string|null} dateString - ISO date string
 * @returns {string} Relative time string
 */
export const getRelativeTime = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    const now = new Date();

    if (isNaN(date.getTime())) {
      return '';
    }

    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;

    // For older dates, return formatted date
    return formatDate(dateString);
  } catch (error) {
    logger.error('Error getting relative time:', error);
    return '';
  }
};
